import { useCallback, useEffect, useState } from "react";
import { useGlobalReducer } from "../../GlobalContext";
import { PicklistOption } from "../blocks/fields/PicklistField.types";
import {
  loadFilteredLookupRecords,
  loadLookupRecords,
  lookupRecordsCache,
} from "./lookupRecords";

const NoOptions: PicklistOption[] = [];

/**
 * The records a field or filter over another entity can pick, read at runtime and held
 * for the session. Undefined entity means a field whose options are in its config. A
 * `searchFilter` asks the service for only the records it names, held apart from the rest.
 */
export function useLookupOptions(
  entityName: string | undefined,
  searchFilter?: string
) {
  const globalDispatch = useGlobalReducer();
  const entity = entityName?.toLowerCase();
  const name = entity && searchFilter ? `${entity}:${searchFilter}` : entity;
  const [options, setOptions] = useState<PicklistOption[]>(
    () => (name && lookupRecordsCache[name]) || NoOptions
  );
  const [loading, setLoading] = useState(
    () => Boolean(name) && !(name! in lookupRecordsCache)
  );
  const [reads, setReads] = useState(0);

  // Drops the held records so the next read goes back to the service.
  const refresh = useCallback(() => {
    if (!name) {
      return;
    }

    delete lookupRecordsCache[name];
    setLoading(true);
    setReads((count) => count + 1);
  }, [name]);

  useEffect(() => {
    if (!name) {
      return undefined;
    }

    if (name in lookupRecordsCache) {
      setOptions(lookupRecordsCache[name]);
      setLoading(false);

      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const read = searchFilter
      ? loadFilteredLookupRecords(globalDispatch, entity!, searchFilter)
      : loadLookupRecords(globalDispatch, [name]);

    read
      .then((loaded) => {
        lookupRecordsCache[name] = loaded[entity!] ?? [];

        if (!cancelled) {
          setOptions(lookupRecordsCache[name]);
        }
      })
      .catch(() => {
        // The service has already said so; an empty dropdown is the failure.
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [globalDispatch, name, entity, searchFilter, reads]);

  return { options, loading, refresh };
}
